import React, { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Box, Stack, Text, Heading, Switch, Textarea, Flex } from "@chakra-ui/react";
import CallTransferSchedule, { defaultSchedule } from "../CallTransferSchedule/CallTransferSchedule";
import FormInput from "../Inputs/FormInput";
import ZaraSelect from "../select/ZaraSelect";
import ZaraButton from "../buttons/Button";
import PhoneInput from "react-phone-input-2";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faBolt } from '@fortawesome/free-solid-svg-icons';


const TRANSFER_RULE_LABEL = "When do you want your agent to transfer calls to this number?";
const TRANSFER_MESSAGE_LABEL = "What do you want your agent to say before it transfers the call?";

function normalizeTransferLabel(field, label) {
    if (field === "instructionBeforTransfer" || label === "Transfer Rule") {
        return TRANSFER_RULE_LABEL;
    }
    if (field === "beforTransferingMsg" || label === "Transfer Message") {
        return TRANSFER_MESSAGE_LABEL;
    }
    return label;
}

function getDefaultValues(values) {
    const defaults = { ...values };
    if (values.warmTransferSettings) {
        defaults.warmTransferSettings = {
            templateMessage: values.warmTransferSettings.templateMessage || null,
        };
    }
    return defaults;
}

function getFieldComponent(type, fieldProps) {
    if (type === "string")
        return (
            <FormInput
                control={fieldProps.control}
                controllName={fieldProps.name}
                label={fieldProps.placeholder}
                {...fieldProps}
            />
        );
    if (type === "textarea")
        return (
            <Controller
                control={fieldProps.control}
                name={fieldProps.name}
                render={({ field }) => (
                    <Textarea
                        placeholder={fieldProps.placeholder}
                        {...field}
                    />
                )}
            />
        );
    if (type === "phonenumber")
        return (
            <Controller
                control={fieldProps.control}
                name={fieldProps.name}
                render={({ field }) => (
                    <PhoneInput
                        inputStyle={{ width: "100%" }}
                        enableSearch={true}
                        country={"us"}
                        inputProps={{ required: true, onBlur: field.onBlur }}
                        value={field.value ?? ""}
                        onChange={value => field.onChange(value)}
                        onBlur={field.onBlur}
                    />
                )}
            />
        );
    if (type === "select")
        return (
            <Controller
                control={fieldProps.control}
                name={fieldProps.name}
                render={({ field }) => {
                    const [isSelectFocus, setIsSelectFocus] = React.useState(false);
                    const sortedOptions = Object.fromEntries(Object.entries(fieldProps.options || {}).sort(([a], [b]) => a === "summary" ? -1 : b === "summary" ? 1 : 0));
                    return (
                        <ZaraSelect
                            selectValue={field.value ?? ""}
                            setSelectValue={field.onChange}
                            placeholder={fieldProps.placeholder || "Select"}
                            isSelectFocus={isSelectFocus}
                            setIsSelectFocus={setIsSelectFocus}
                        >
                            {Object.entries(sortedOptions).map(([key, val]) => (
                                <option key={key} value={key}>
                                    {val}
                                </option>
                            ))}
                        </ZaraSelect>
                    );
                }}
            />
        );
    return null;
}

const CallTransferEdit = ({
    values = {},
    fieldLabelTypes = {},
    onSave,
    onClose,
    displayType = "dialog",
    open = true,
    title = "Edit Row",
    showTimeBoundingBox = false,
    isWarmTransferLocked = false,
    onUpgrade,
}) => {
    const { control, handleSubmit, reset, setValue } = useForm({
        defaultValues: getDefaultValues(values),
    });

    const instructionBeforTransfer = useWatch({ control, name: "instructionBeforTransfer", defaultValue: "" });
    const templateMessage = useWatch({ control, name: "warmTransferSettings.templateMessage", defaultValue: null });
    const placeHolderTemplateMessage = "You are a transfer assistant. Follow these rules exactly:\n1. Confirm the person who answers is a live operator. If they say they need a moment or need to place you on hold, acknowledge briefly and wait silently until they return. If you detect voicemail at any time, call `transferCancel`.\n2. Once the operator is available, provide a concise 1–2 sentence summary of the caller's reason for escalation.\n3. After providing the summary, ask: \"Would you like to take this call?\" If they accept, call `transferSuccessful`. If they decline, call `transferCancel`. If the operator asks further questions, answer them to the best of your ability but do not fabricate/make up anything.\n4. Do not ask additional questions, do not engage in small talk, and do not repeat information unless the operator explicitly asks.\nAlways provide the mandatory details, always wait through operator holds, and always secure a clear accept/decline decision.";
    // State for schedule
    const [schedule, setSchedule] = React.useState(values.preferredSchedule || null);
    const [loading, setLoading] = React.useState(false);
    const [warmTransferEnabled, setWarmTransferEnabled] = React.useState(!!values.warmTransferSettings);

    useEffect(() => {
        if (isWarmTransferLocked) {
            setWarmTransferEnabled(false);
        }
    }, [isWarmTransferLocked]);

    useEffect(() => {
        reset(getDefaultValues(values));
        if (values.warmTransferSettings !== undefined) {
            setWarmTransferEnabled(!!values.warmTransferSettings);
        }
    }, [values, open, reset]);

    // Keep form fields in sync when warm transfer is toggled on/off
    useEffect(() => {
        if (warmTransferEnabled) {
            setValue("warmTransferSettings.templateMessage", (values.warmTransferSettings && values.warmTransferSettings.templateMessage) || "");
        } else {
            setValue("warmTransferSettings.templateMessage", undefined);
        }
    }, [warmTransferEnabled, setValue, values]);

    // Prefill template message when enabled and template is empty
    useEffect(() => {
        if (warmTransferEnabled && templateMessage === null && instructionBeforTransfer) {
            setValue("warmTransferSettings.templateMessage", placeHolderTemplateMessage);

        }
    }, [instructionBeforTransfer, templateMessage, warmTransferEnabled, setValue]);



    if (displayType === "dialog" && !open) return null;

    function handleFormSubmit(data) {
        setLoading(true);
        const preferredSchedule = schedule || { ...defaultSchedule, enabled: false };
        const warmTransferSettings = !isWarmTransferLocked && warmTransferEnabled ? {
            typeWarmTransfer: "template",
            onNoPickup: "hangup",
            templateMessage: data.warmTransferSettings?.templateMessage || "",
        } : null;
        const saveValues = { ...data, preferredSchedule, warmTransferSettings };
        Promise.resolve(onSave && onSave(saveValues))
            .finally(() => setLoading(false));

    }

    function renderFields() {
        const sections = {
            basics: {},
            callerIdentity: {},
            advancedBehavior: {},
            warmTransfer: {},
        };

        Object.entries(fieldLabelTypes).forEach(([field, config]) => {
            if (field === "phoneNumber" || field === "instructionBeforTransfer" || field === "beforTransferingMsg") {
                sections.basics[field] = config;
            } else if (field === "callerIdStatus") {
                sections.callerIdentity[field] = config;
            } else if (field === "warmTransferSettings") {
                sections.warmTransfer[field] = config;
            } else {
                sections.advancedBehavior[field] = config;
            }
        });

        const renderSection = (title, fields, icon) => {
            if (Object.keys(fields).length === 0) return null;

            return (
                <Box key={title}>
                    <Flex align="center" gap={2} mb={4}>
                        {icon && <FontAwesomeIcon icon={icon} fontSize="16px" />}
                        <Text fontSize="sm" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
                            {title}
                        </Text>
                    </Flex>
                    <Stack spacing={4}>
                        {Object.entries(fields).map(([field, config]) => {
                            if (config.type === "group" && field === "warmTransferSettings") {
                                return (
                                    <Box key={field}>
                                        <Stack direction="row" align="center" spacing={2} mb={2}>
                                            <Switch
                                                isChecked={warmTransferEnabled}
                                                isDisabled={isWarmTransferLocked}
                                                onChange={(e) => setWarmTransferEnabled(e.target.checked)}
                                            />
                                            <Text fontWeight="600">{config.label}</Text>
                                            {isWarmTransferLocked && (
                                                <Box
                                                    as="button"
                                                    type="button"
                                                    onClick={onUpgrade}
                                                    ml="auto"
                                                    px={4}
                                                    py={2}
                                                    borderRadius="999px"
                                                    color="white"
                                                    fontSize="xs"
                                                    fontWeight="700"
                                                    textTransform="uppercase"
                                                    bgGradient="linear(to-r, #8B5CF6, #EC4899)"
                                                >
                                                    ✨ Upgrade to Access
                                                </Box>
                                            )}
                                        </Stack>
                                        {warmTransferEnabled && !isWarmTransferLocked && (
                                            <Box borderRadius={8} p={3} borderColor="gray.200">
                                                <Controller
                                                    control={control}
                                                    name="warmTransferSettings.templateMessage"
                                                    render={({ field: f }) => (
                                                        <Textarea
                                                            placeholder={templateMessage === '' ? placeHolderTemplateMessage : templateMessage}
                                                            {...f}
                                                            minH="100px"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                );
                            }

                            const { label, type, values: options } = config;
                            const normalizedLabel = normalizeTransferLabel(field, label);
                            return (
                                <Box key={field}>
                                    {type !== "string" && (
                                        <Text fontWeight="600" mb={2}>{normalizedLabel}</Text>
                                    )}
                                    {getFieldComponent(type, {
                                        control,
                                        name: field,
                                        placeholder: normalizedLabel,
                                        options,
                                    })}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            );
        };

        return (
            <Flex gap={6} flexDirection={{ base: "column", lg: "row" }}>
                <Box flex={1}>
                    {renderSection("BASICS", sections.basics, faPhone)}
                    <Box mt={6}>
                        {renderSection("CALLER IDENTITY", sections.callerIdentity)}
                    </Box>
                </Box>
                <Box flex={1}>
                    <Box>
                        {renderSection("ADVANCED BEHAVIOR", { ...sections.advancedBehavior, ...sections.warmTransfer }, faBolt)}
                        {showTimeBoundingBox && (
                            <Box mt={6} bg="blue.50" borderRadius={8} p={4}>
                                <CallTransferSchedule value={schedule} onChange={setSchedule} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Flex>
        );
    }

    return displayType === "dialog" ? (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
            }}
        >
            <form
                onSubmit={handleSubmit(handleFormSubmit)}
                style={{
                    background: "#fff",
                    padding: 32,
                    borderRadius: 12,
                    minWidth: 320,
                    maxWidth: 1000,
                    width: '95vw',
                    maxHeight: 'calc(100vh - 40px)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box mb={6}>
                    <Heading as="h2" size="lg" fontWeight="bold" mb={1}>
                        {title}
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                        Configure how calls are routed to external agents or numbers.
                    </Text>
                </Box>
                <Stack spacing={6} style={{ flex: '1 1 auto', overflow: 'hidden' }}>
                    <div style={{ overflowY: 'auto', paddingRight: 8, maxHeight: 'calc(100vh - 280px)' }}>
                        {renderFields()}
                    </div>
                    <Stack direction="row" justify="flex-end" spacing={2} style={{ flex: '0 0 auto' }}>
                        <ZaraButton variant="secondary" onClick={onClose} type="button">
                            Cancel
                        </ZaraButton>
                        <ZaraButton variant="primary" type="submit" disabled={loading} isLoading={loading}>
                            Save Changes
                        </ZaraButton>
                    </Stack>
                </Stack>
            </form>
        </div>
    ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Stack spacing={4}>
                <Box mb={4}>
                    <Heading as="h2" size="md" fontWeight="semibold" mb={1}>
                        {title}
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                        Configure how calls are routed to external agents or numbers.
                    </Text>
                </Box>
                {renderFields()}
                {showTimeBoundingBox && (
                    <CallTransferSchedule value={schedule} onChange={setSchedule} />
                )}
                <Stack direction="row" justify="flex-end" spacing={2}>
                    <ZaraButton variant="secondary" onClick={onClose} type="button">
                        Cancel
                    </ZaraButton>
                    <ZaraButton variant="primary" type="submit" disabled={loading} isLoading={loading}>
                        Save
                    </ZaraButton>
                </Stack>
            </Stack>
        </form>
    );
};

export default CallTransferEdit;
